/**
 * CetusEd Admin Console — Support Ticket List
 *
 * COMPLIANCE: Support tickets may reference student PII when troubleshooting.
 *   All support staff with ticket access must have a valid DPA and completed
 *   FERPA training per §1232g(b)(4)(B).
 */

import React, { useState } from 'react';
import { useSupportTickets } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { SupportTicket } from '../../api/hooks';

export const TicketList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const { data: tickets, isLoading, error } = useSupportTickets(statusFilter || undefined);

  const columns: Column<SupportTicket>[] = [
    { key: 'id', header: 'ID', width: '100px' },
    { key: 'districtName', header: 'District', sortable: true },
    { key: 'subject', header: 'Subject', sortable: true },
    { key: 'status', header: 'Status', sortable: true, render: (row) => (
      <span className={`status-badge status-badge--${row.status === 'open' ? 'provisioning' : row.status === 'in_progress' ? 'active' : row.status === 'resolved' ? 'active' : row.status === 'closed' ? 'deactivated' : 'past_due'}`}>
        {row.status.replace('_', ' ')}
      </span>
    )},
    { key: 'priority', header: 'Priority', sortable: true, render: (row) => (
      <span className={`priority-badge priority-badge--${row.priority}`}>{row.priority}</span>
    )},
    { key: 'createdBy', header: 'Created By' },
    { key: 'createdAt', header: 'Created', render: (row) => new Date(row.createdAt).toLocaleDateString() },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load support tickets.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Support Tickets</h1>
        <div className="filter-group">
          <label htmlFor="ticket-status" className="sr-only">Filter by status</label>
          <select id="ticket-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={tickets || []}
        keyExtractor={(t) => t.id}
        isLoading={isLoading}
        emptyMessage="No support tickets found."
      />
    </div>
  );
};
